import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import styled from '@emotion/styled';

import BackToJobListButton from '../../../components/BackToJobListButton/BackToJobListButton';
import PageBanner from '../../../components/Banner/PageBanner';
import PaginationControl from '../../../components/PaginationControl/PaginationControl';
import { BOOKMARKS } from '../../../routes/routeConstants';
import JobInfo from './JobInfo/JobInfo';
import JobSearchForm from './JobSearchForm/JobSearchForm';
import JobsList from './JobsList/JobsList';
import { useAppSelector } from '../../../redux/store';
import Page from '../../../components/Page/Page';
import { ModalProvider } from '../../../context/ModalContext';
import ModalTriggerContainer from '../../../components/Modals/ModalTriggerContainer';
import { Modal } from '../../../components/Modals/Modal';
import InitialFeedbackModal from '../../../components/Modals/ModalsActions/Feedback/InitialFeedbackModal';
import MonthlyFeedbackModal from '../../../components/Modals/ModalsActions/Feedback/MonthyFeedbackModal';
import { isAtLeast30DaysAgo, isAtLeast7DaysAgo } from '../../../utils/utils';
import { useJobSearch } from '../../../context/JobSearchContext';
import useQueryString from '../../../hooks/ui-control/useQueryString';

const ShowModal = React.forwardRef<HTMLDivElement>((_, ref) => {
  const showInitial = useAppSelector(
    (state) => state.preferences.feedback.showInitialPrompt
  );

  return (
    <ModalProvider>
      <ModalTriggerContainer ref={ref}>
        <></>
      </ModalTriggerContainer>
      <Modal>
        {showInitial ? <InitialFeedbackModal /> : <MonthlyFeedbackModal />}
      </Modal>
    </ModalProvider>
  );
});

const LocumJobsContent: React.FC = () => {
  const navigate = useNavigate();

  // Job search context (loading, pagination)
  const { loading, page, setPage, perPage } = useJobSearch();

  // Mobile info toggle state
  const [showMobileInfo, setShowMobileInfo] = useState(false);

  // Redux selectors
  const totalPages = useAppSelector((state) => state.locumJobs.totalPages);
  const allJobs = useAppSelector((state) => state.locumJobs.jobData);

  // Authentication data for feedback prompts
  const authData = useAppSelector((state) => state.auth.authData);
  const showInitialPrompt = useAppSelector(
    (state) => state.preferences.feedback.showInitialPrompt
  );
  const showMonthlyPrompt = useAppSelector(
    (state) => state.preferences.feedback.showMonthlyPrompt
  );

  // URL query params for jobId and invite flag
  const [, setQueryParams] = useQueryString();

  // Feedback prompt timing
  const canShowMonthlyPrompt = isAtLeast30DaysAgo(
    authData?.lastInitialPromptedAt ?? ''
  );
  const canShowMonthlyPromptAgain =
    authData?.lastMonthlyDeclinedAt == null
      ? true
      : isAtLeast7DaysAgo(authData?.lastMonthlyDeclinedAt ?? '');

  const triggerRef = useRef<HTMLDivElement>(null);

  /**
   * Show feedback modals based on preferences and session flags
   */
  useEffect(() => {
    const initialFired = sessionStorage.getItem('SIFP') === 'true';
    const monthlyFired = sessionStorage.getItem('SMFP') === 'true';

    if (
      (showInitialPrompt && initialFired) ||
      (showMonthlyPrompt &&
        monthlyFired &&
        canShowMonthlyPrompt &&
        canShowMonthlyPromptAgain)
    ) {
      triggerRef.current?.click();
    }
  }, [
    page,
    perPage,
    showInitialPrompt,
    showMonthlyPrompt,
    canShowMonthlyPrompt,
    canShowMonthlyPromptAgain,
  ]);

  /**
   * Handler: change page and collapse mobile view on small screens
   */
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage.toString());
      if (window.innerWidth < 885) setShowMobileInfo(false);
    },
    [setPage]
  );

  /**
   * Auto-select the first job whenever the job list changes (e.g., on search or page change)
   * Skips if an invite is active or a jobId is already selected.
   */

  // TODO: handle case where a job is shared so the id of that job will not be replaced by that automatic first job pick
  useEffect(() => {
    if (allJobs.length > 0) {
      setQueryParams({ jobId: allJobs[0].id });
    } else {
      setQueryParams({ jobId: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allJobs, page]);

  return (
    <Page title="Locumspace | Jobs">
      <ShowModal ref={triggerRef} />

      <Shell>
        <PageBanner
          label="Locum Jobs"
          description="Browse jobs available on Locumspace"
          // className={`onlyMobileScreens ${showMobileInfo ? 'hide' : ''}`}
        />

        <JobSearchForm showMobileInfo={showMobileInfo} />

        <NavArea>
          <BackToJobListButton
            showMobileInfo={showMobileInfo}
            setShowMobileInfo={setShowMobileInfo}
          />
          <GoToBookmark onClick={() => navigate(`/${BOOKMARKS}`)}>
            Go to Bookmarks
          </GoToBookmark>
        </NavArea>

        <Container>
          <JobViewArea>
            <JobsList
              showMobileInfo={showMobileInfo}
              setShowMobileInfo={setShowMobileInfo}
              loading={loading}
              guest={false}
            />
            <JobInfo showMobileInfo={showMobileInfo} guest={false} />
          </JobViewArea>

          <PaginationShell>
            <PaginationControl
              endPage={totalPages || 1}
              changePage={handlePageChange}
              defaultNumOfPagination={5}
              currentPage={parseInt(page, 10)}
            />
          </PaginationShell>
        </Container>
      </Shell>
    </Page>
  );
};

export default LocumJobsContent;

// ------ Styled Components ------

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 54px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  background-color: ${({ theme }) => theme.palette.backgroundColor};
  padding: 2rem;
  border-radius: 0.75rem;

  @media (max-width: 1024px) {
    padding: 0;
    margin-left: -30px;
    margin-right: -30px;
  }

  @media (max-width: 884px) {
    margin-left: 0;
    margin-right: 0;
  }

  @media (max-width: 540px) {
    /* border: 1px solid yellow; */
    padding: 20px;
    border-radius: 0;
    // background-color: white;
    background-color: ${({ theme }) => theme.palette.backgroundColor};
    margin-left: -20px;
    margin-right: -20px;
  }

  @media (max-width: 480px) {
    overflow: hidden;
  }
`;

const JobViewArea = styled.div`
  display: grid;
  grid-template-columns: 316px 1fr;
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 300px 1fr;
  }

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
  }
`;

const PaginationShell = styled.div`
  display: grid;
  place-content: center;
  grid-column: span 2;

  @media (max-width: 884px) {
  }
`;

const NavArea = styled.div`
  display: flex;
  margin-top: 20px;
`;

const GoToBookmark = styled.div`
  /* border: 1px solid red; */
  margin-left: auto;
  font-weight: 500;
  font-size: 18px;
  font-weight: 500;
  color: #2f80ed;
  cursor: pointer;

  @media (min-width: 885px) {
    display: none;
  }
`;
